/*
 * Strubbel offer picker.
 * Writes the chosen option's variant id into the theme's product form so the normal
 * add-to-cart flow (cart drawer) is reused, and swaps the buy button for the
 * "Bald wieder verfügbar" form when the chosen option is sold out.
 */
if (!customElements.get('strubbel-offer')) {
  class StrubbelOffer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.info = this.closest('product-info') || document.getElementById(`ProductInfo-${this.sectionId}`);
      this.inputs = Array.from(this.querySelectorAll('input[type="radio"][data-variant-id]'));
      this.waitlist = this.querySelector('.sb-waitlist');
      this.inputs.forEach((input) => input.addEventListener('change', () => this.apply(true)));
      this.apply(false);
      this.startPromoTimer();
      // Fly-to-cart animation whenever the theme form adds something to the cart
      (this.info || document).querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
        form.addEventListener('submit', () => this.flyToCart(), true);
      });
    }

    // A small round photo of the chosen product flies from the buy button into the cart icon
    flyToCart() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const option = this.selected;
      const thumb = option && option.closest('.sb-option').querySelector('.sb-option__thumb img');
      const button = (this.info || document).querySelector('.product-form__submit[name="add"]');
      const src = thumb && (thumb.currentSrc || thumb.src);
      if (!src || !button) return;

      const size = 64;
      const from = button.getBoundingClientRect();
      const cart = document.getElementById('cart-icon-bubble');
      let to = cart ? cart.getBoundingClientRect() : null;
      if (!to || to.width === 0 || to.bottom < 0 || to.top > window.innerHeight) {
        to = { left: window.innerWidth - 56, top: 16, width: 32, height: 32 }; // header scrolled away: aim at the top-right corner
      }
      const x0 = from.left + from.width / 2 - size / 2;
      const y0 = from.top + from.height / 2 - size / 2;
      const dx = to.left + to.width / 2 - size / 2 - x0;
      const dy = to.top + to.height / 2 - size / 2 - y0;

      const ball = document.createElement('img');
      ball.src = src;
      ball.alt = '';
      ball.className = 'sb-fly';
      ball.style.left = `${x0}px`;
      ball.style.top = `${y0}px`;
      document.body.appendChild(ball);

      const flight = ball.animate(
        [
          { transform: 'translate(0, 0) scale(0.4)', opacity: 0 },
          { transform: 'translate(0, -12px) scale(1.1)', opacity: 1, offset: 0.15 },
          { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 90}px) scale(0.8)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${dx}px, ${dy}px) scale(0.2)`, opacity: 0.4 },
        ],
        { duration: 800, easing: 'cubic-bezier(0.45, 0, 0.25, 1)' }
      );
      flight.onfinish = () => {
        ball.remove();
        if (cart) {
          cart.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.35)' }, { transform: 'scale(1)' }], {
            duration: 380,
            easing: 'ease-out',
          });
        }
      };
    }

    // Real countdown for a time-limited promotion; hides the banner when it ends
    startPromoTimer() {
      const promo = this.querySelector('.sb-promo[data-end]');
      const end = promo ? Number(promo.dataset.end) * 1000 : 0;
      if (!end) return;
      const timer = promo.querySelector('.sb-promo__timer');
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          promo.remove();
          clearInterval(this.promoInterval);
          return;
        }
        const sec = Math.floor(left / 1000);
        timer.querySelector('[data-days]').textContent = Math.floor(sec / 86400);
        timer.querySelector('[data-hours]').textContent = pad(Math.floor((sec % 86400) / 3600));
        timer.querySelector('[data-minutes]').textContent = pad(Math.floor((sec % 3600) / 60));
        timer.querySelector('[data-seconds]').textContent = pad(sec % 60);
        timer.hidden = false;
      };
      tick();
      this.promoInterval = setInterval(tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.promoInterval);
    }

    get selected() {
      return this.inputs.find((input) => input.checked) || this.inputs[0];
    }

    apply(fromUser) {
      const option = this.selected;
      if (!option) return;
      const available = option.dataset.available === 'true';
      const scope = this.info || document;

      this.inputs.forEach((input) => input.closest('.sb-option').classList.toggle('is-selected', input === option));

      // Variant that the theme's product form will add to the cart
      scope.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"]').forEach((idInput) => {
        idInput.value = option.dataset.variantId;
      });

      // Buy button state, label and price
      scope.querySelectorAll('.product-form__submit[name="add"]').forEach((button) => {
        button.disabled = !available;
        button.toggleAttribute('data-unavailable', !available);
        const label = button.querySelector('.main-atc__label__text');
        if (label) label.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldout;
        const price = button.querySelector('.main-atc-price');
        if (price) {
          price.textContent = `${option.dataset.price} •`;
          price.classList.toggle('hidden', !available);
        }
      });

      // Sold out: hide the buy button, show the waitlist form
      const initialStyle = this.querySelector('style[data-sb-initial]');
      if (initialStyle) initialStyle.remove();
      if (this.info) this.info.classList.toggle('sb-soldout', !available);
      if (this.waitlist) this.waitlist.hidden = available;

      const tags = this.querySelector('input[name="contact[tags]"]');
      if (tags) tags.value = `warteliste, warteliste-${option.dataset.handle}`;
      this.querySelectorAll('[data-waitlist-product]').forEach((el) => {
        el.textContent = option.dataset.title;
      });

      this.filterMedia(option, fromUser);
    }

    // Show only the photos of the chosen option (shampoo: front/back, pack: the three + box).
    // Uses the theme's own "hidden" class, like its variant image filtering.
    filterMedia(option, fromUser) {
      const ids = (option.dataset.mediaIds || '').split(',').filter(Boolean);
      const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      if (!gallery || !ids.length) return;
      try {
        const wanted = new Set(ids.map((id) => `${this.sectionId}-${id}`));
        const slides = Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]'));
        slides.forEach((el) => el.classList.toggle('hidden', !wanted.has(el.dataset.mediaId)));
        gallery.querySelectorAll('.thumbnail-list__item[data-target]').forEach((el) => {
          el.classList.toggle('hidden', !wanted.has(el.dataset.target));
        });
        gallery.querySelectorAll('.slider-counter__link--dots').forEach((dot, i) => {
          if (slides[i]) dot.classList.toggle('hidden', slides[i].classList.contains('hidden'));
        });
        const first = slides.find((el) => wanted.has(el.dataset.mediaId));
        if (first && (fromUser || !first.classList.contains('is-active'))) {
          gallery.setActiveMedia(first.dataset.mediaId, false);
          first.parentElement.scrollTo({ left: 0 });
        }
        ['GalleryViewer', 'GalleryThumbnails'].forEach((prefix) => {
          const slider = gallery.querySelector(`[id^="${prefix}"]`);
          if (slider && slider.initPages) {
            slider.initPages();
            if (slider.update) slider.update();
          }
        });
      } catch (error) {
        /* gallery markup differs: leave all photos visible */
      }
    }
  }

  customElements.define('strubbel-offer', StrubbelOffer);
}
